import type { entityVersions } from '@clockwise/clients';
import { AlertTriangle, ArrowRight, Info } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

type ApplyTo = entityVersions.ApplyTo;
type EditImpact = entityVersions.EditImpact;
type VersionFieldChange = entityVersions.VersionFieldChange;
type Restated = entityVersions.Restated;

/**
 * "When did this become true?" — asked when an employee or customer edit would
 * change what logged timesheets report.
 *
 * An ordinary save starts a new version today, so every entry already logged
 * keeps the old value. That is wrong when the old value was a mistake or a
 * blank nobody filled in (the 2026-09-28 Pay Type incident), and nothing about
 * the form said so. The question makes the choice visible and explicit: one
 * question per save, answered for every field that moved together.
 *
 * Copy follows the agreed prototype ("When Did This Become True?" artifact):
 * the options say what was true for the person or customer; the line under
 * each says what happens to their entries.
 */

/** A value as a person reads it. Foreign keys arrive with a resolved label. */
export function displayValue(value: unknown, label?: string | null): string | null {
  if (label) return label;
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  return 'updated';
}

/**
 * Yes/no fields read as a state of the record, not a value of a field: "Logan
 * has always been inactive", never "Logan's active has always been No".
 * Keyed by field; `[when true, when false]`.
 */
const STATES: Readonly<Record<string, readonly [string, string]>> = {
  active: ['active', 'inactive'],
  isInternal: ['an internal customer', 'not an internal customer'],
};

/** The value as it reads in a sentence: a yes/no field's state, else the value. */
function phrase(c: VersionFieldChange, value: unknown, label?: string | null): string | null {
  const states = STATES[c.field];
  if (states && typeof value === 'boolean') return value ? states[0] : states[1];
  return displayValue(value, label);
}

const fromOf = (c: VersionFieldChange) => phrase(c, c.from, c.fromLabel);
const toOf = (c: VersionFieldChange) => phrase(c, c.to, c.toLabel);

/** "Aug 31, 2026" from a `YYYY-MM-DD` work date — no timezone shift. */
export function formatWorkDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1)).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

const monthName = (ym: string, withYear: boolean) => {
  const [y, m] = ym.split('-').map(Number);
  return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, 1)).toLocaleDateString('en-US', {
    month: 'long',
    ...(withYear ? { year: 'numeric' } : {}),
    timeZone: 'UTC',
  });
};

const monthIndex = (ym: string) => {
  const [y, m] = ym.split('-').map(Number);
  return (y ?? 0) * 12 + (m ?? 0);
};

/**
 * "August 2026", "July and August 2026", "January to August 2026",
 * "December 2025 and January 2026". A run of three or more consecutive months
 * reads as a range; anything else is listed.
 */
export function formatMonths(months: readonly string[]): string {
  const sorted = [...months].sort();
  if (sorted.length === 0) return '';
  const first = sorted[0] as string;
  const last = sorted[sorted.length - 1] as string;
  const oneYear = first.slice(0, 4) === last.slice(0, 4);
  const name = (ym: string, isLast: boolean) => monthName(ym, !oneYear || isLast);
  const consecutive = monthIndex(last) - monthIndex(first) === sorted.length - 1;
  if (sorted.length >= 3 && consecutive) return `${name(first, false)} to ${name(last, true)}`;
  const names = sorted.map((m, i) => name(m, i === sorted.length - 1));
  if (names.length === 1) return names[0] as string;
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * A field label as it reads mid-sentence. Plain labels lowercase
 * ("Employee type" → "employee type"); a label carrying an abbreviation or a
 * name keeps its capitals ("SR Controller", "Keka employee ID") — lowercasing
 * those produces "sr controller".
 */
export function inSentence(label: string): string {
  return /[A-Z]/.test(label.slice(1)) ? label : label.charAt(0).toLowerCase() + label.slice(1);
}

/** "Saloni's", "Acme Robotics'" — a name already ending in s takes only the
 *  apostrophe. */
export function possessive(name: string): string {
  return /s$/i.test(name) ? `${name}'` : `${name}'s`;
}

/** The two option labels. One field names it; several share one sentence. */
export function optionLabels(
  subject: string,
  changes: readonly VersionFieldChange[],
): { always: string; today: string } {
  if (changes.length !== 1) {
    return {
      always: `These have always been true for ${subject}`,
      today: 'These are true from today',
    };
  }
  const c = changes[0] as VersionFieldChange;
  const to = toOf(c) ?? 'blank';
  if (STATES[c.field]) {
    return {
      always: `${subject} has always been ${to}`,
      today: `${subject} is ${to} from today`,
    };
  }
  const field = inSentence(c.label);
  return {
    always: `${possessive(subject)} ${field} has always been ${to}`,
    today: `${possessive(subject)} ${field} is ${to} from today`,
  };
}

const entriesWord = (n: number) => (n === 1 ? 'timesheet entry' : 'timesheet entries');

/**
 * What the toast says after the save, so nothing changes without saying so.
 * The counts come from the save's own response (`restated`, counted in its
 * transaction); the preview's are only a fallback for a server that sent none.
 */
export function savedMessage(
  impact: EditImpact,
  applyTo: ApplyTo,
  restated?: Restated | null,
): string {
  if (applyTo === 'today')
    return 'Saved. Applies from today. Earlier timesheet entries are unchanged.';
  const single = impact.changes.length === 1 ? impact.changes[0] : undefined;
  const what = single ? (toOf(single) ?? 'the new value') : 'the new values';
  const count = restated?.entryCount ?? impact.entryCount;
  const first = restated ? restated.firstEntryDate : impact.firstEntryDate;
  const since = first ? ` since ${formatWorkDate(first)}` : '';
  return `Saved. ${count} ${entriesWord(count)}${since} now show ${what}.`;
}

/** True when this edit should stop and ask before it saves. */
export function shouldAsk(impact: EditImpact): boolean {
  return impact.canApplyToCurrentVersion && impact.changes.length > 0 && impact.entryCount > 0;
}

export function ApplyChangeDialog({
  open,
  subject,
  subjectName,
  impact,
  pending,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  /** How the options name the record: an employee's first name, a customer's
   *  name. */
  subject: string;
  /** Full name for the sentence under the title. */
  subjectName: string;
  impact: EditImpact | null;
  pending: boolean;
  onCancel: () => void;
  onConfirm: (applyTo: ApplyTo) => void;
}) {
  const [choice, setChoice] = useState<ApplyTo>(impact?.suggested ?? 'today');
  const name = useId();
  // Each new question starts on its own suggestion, not the last answer.
  useEffect(() => {
    if (impact) setChoice(impact.suggested);
  }, [impact]);

  if (!impact) return null;
  const many = impact.changes.length > 1;
  const labels = optionLabels(subject, impact.changes);
  const single = many ? undefined : impact.changes[0];
  const newWhat = single ? (toOf(single) ?? 'no value') : 'the new values';
  const oldWhat = single ? (fromOf(single) ?? 'no value') : 'the old values';
  const since = impact.firstEntryDate ? formatWorkDate(impact.firstEntryDate) : null;
  const n = impact.entryCount;
  // Why "from today" is suggested when blank and filled fields are mixed.
  const filled =
    many && impact.suggested === 'today'
      ? impact.changes.find((c) => fromOf(c) !== null)
      : undefined;
  const mixed = filled !== undefined && impact.changes.some((c) => fromOf(c) === null);
  const closed = impact.closedMonths;

  const option = (value: ApplyTo, title: string, detail: string) => (
    <label
      className={cn(
        'flex cursor-pointer items-start gap-3 rounded-md border px-3 py-2.5',
        choice === value ? 'border-primary bg-primary/5' : 'border-border',
      )}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={choice === value}
        onChange={() => setChoice(value)}
        className="mt-1"
      />
      <span className="flex flex-col gap-0.5">
        <span className="text-sm font-medium">
          {title}
          {impact.suggested === value ? (
            <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs font-normal text-muted-foreground">
              Suggested
            </span>
          ) : null}
        </span>
        <span className="text-sm text-muted-foreground">{detail}</span>
      </span>
    </label>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => (!o && !pending ? onCancel() : undefined)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>When did this become true?</DialogTitle>
          <DialogDescription>
            {subjectName} has {n} {entriesWord(n)}
            {since ? ` since ${since}` : ''}. Your answer decides what they show.
          </DialogDescription>
        </DialogHeader>

        <dl className="rounded-md border px-3 py-1 text-sm">
          {impact.changes.map((c) => (
            <div key={c.field} className="grid grid-cols-[8rem_minmax(0,1fr)] gap-2 py-1">
              <dt className="text-muted-foreground">{c.label}</dt>
              <dd className="flex flex-wrap items-center gap-1">
                {c.restricted ? (
                  <span className="text-muted-foreground">Hidden</span>
                ) : (
                  <>
                    <span className={fromOf(c) === null ? 'text-muted-foreground italic' : ''}>
                      {fromOf(c) ?? 'blank'}
                    </span>
                    <ArrowRight className="size-3 text-muted-foreground" aria-label="changes to" />
                    <span className="font-medium">{toOf(c) ?? 'blank'}</span>
                  </>
                )}
              </dd>
            </div>
          ))}
        </dl>

        <fieldset className="flex flex-col gap-2">
          <legend className="sr-only">When did this become true?</legend>
          {option(
            'current_version',
            labels.always,
            `All ${n} ${entriesWord(n)}${since ? ` since ${since}` : ''} will show ${newWhat}.`,
          )}
          {option(
            'today',
            labels.today,
            `Entries before today keep ${many ? oldWhat : `showing ${oldWhat}`}. New entries use ${newWhat}.`,
          )}
        </fieldset>

        {many ? (
          <p className="flex gap-2 text-sm text-muted-foreground">
            <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            <span>
              {mixed && filled ? `Suggested because ${filled.label} already had a value. ` : ''}
              If only some of these were always true, go back and save them separately.
            </span>
          </p>
        ) : null}

        {choice === 'current_version' && closed.length > 0 ? (
          <p
            role="alert"
            className="flex gap-2 rounded-md border border-amber-500/50 bg-amber-50 px-3 py-2 text-sm dark:bg-amber-950/30"
          >
            <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            <span>
              <strong>
                {formatMonths(closed)} {closed.length === 1 ? 'is' : 'are'} already closed.
              </strong>{' '}
              Reports and dashboards for {closed.length === 1 ? 'that month' : 'those months'} will
              change. Invoices already sent won't.
            </span>
          </p>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
            Back to editing
          </Button>
          <Button type="button" onClick={() => onConfirm(choice)} disabled={pending}>
            {pending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
